import { splitArrayEvenly } from '@Components/Utilities/ArrayHelpers';
import { useState, useEffect, Fragment, useMemo } from 'react';
import { bannerAdPositions, contactType, inquiryType, gtmEventType } from '@Components/Utilities/SandhillsConstants';
import { getBannerAd, gtmPushEvent, repositionCookieBanner, getURLParameter, insertURLParameter, removeURLParameter, chunkArray, syncCanonicalUrl } from '@Components/Utilities/SandhillsUtility';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import FacetedSearchMobileMenu from '@Components/Shared/FacetedSearch/FacetedSearchMobileMenu';
import ListingControlBar from '@Components/Listings/ListingControlBar';
import Paging from '@Components/MuiWrappers/Paging';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import ComplexBreadcrumb from '@Components/Shared/ComplexBreadcrumb';
import DoubleClickAd from '@Components/Shared/DoubleClick/DoubleClickAd';
import PartsListListing from '@Components/Parts/PartsListListing';
import PartsGridListing from '@Components/Parts/PartsGridListing';
import PartsCategoryLinks from '@Components/Parts/PartsCategoryLinks';
import FacetedSearch from '@Components/Shared/FacetedSearch/FacetedSearch';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue, useRecoilState } from 'recoil';
import AdditionalPageInformation from '@Components/Listings/AdditionalPageInformation';
import ListingOptions from '@Components/Shared/ListingOptions';
import debounce from 'lodash.debounce';
import PartsSearch from '@Components/Shared/PartsSearch';

const EmailModal = loadable(() => import('@Components/Shared/Modals/EmailModal'), true);
const DealerHeaderInfo = loadable(() => import('@Components/Shared/DealerHeaderInfo'));

const PartsListView = (props) => {

    const constants = { searchType: 'SearchType', page: 'page', format: 'Format', keywords: 'Keywords', manufacturer: 'Manufacturer', model: 'Model', showOnly: 'ShowOnly', condition: 'Condition', dsCompanyID: 'DSCompanyID', locationID: "LocationID" };
    const topBannerAd = getBannerAd(props.DoubleClickAdCollection?.AdUnits, bannerAdPositions.Top);
    const middleBannerAd = getBannerAd(props.DoubleClickAdCollection?.AdUnits, bannerAdPositions.Middle);
    const bottomBannerAd = getBannerAd(props.DoubleClickAdCollection?.AdUnits, bannerAdPositions.Bottom);
    const middleAdIndex = props.PartsListings.length >= props.DoubleClickAdShowAfterNumberOfListings ? props.DoubleClickAdShowAfterNumberOfListings - 1 : props.PartsListings.length - 1;
    const bottomAdIndex = props.DoubleClickAdShowSecondAdAfterNumberOfListings;
    const [partNumber, setPartNumber] = useState(props.PartNumber);
    const [keywords, setKeywords] = useState(props.Keywords);
    const [searchFields, setSearchFields] = useState(props.SearchFields);
    const [searchInProgress, setSearchInProgress] = useState(false);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [mobileSearch, setMobileSearch] = useState(false);
    const [samModel, setSamModel] = useState(props.SAMModel);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [loadImagesForPrint, setLoadImagesForPrint] = useState(false);
    const [selectedInquiry, setSelectedInquiry] = useState('');
    const [selectedContactType, setSelectedContactType] = useState(contactType.Retail);
    const [returnFocusRef, setReturnFocusRef] = useState({});
    const [showShareButtons, setShowShareButtons] = useState(false);
    const [newView, setNewView] = useState(props.UseNewListPageDesign);
    const windowSize = useWindowSize();
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);
    const [listingPictureMap, setListingPictureMap] = useState({});
    const [currentUser, setCurrentUser] = useRecoilState(atoms.currentUser);
    const [expandAllDescriptions, setExpandAllDescriptions] = useState(props.ShowFullDescription);
    const [showAllSpecs, setShowAllSpecs] = useState(props.ShowAllSpecs);

    useEffect(() => {
        const cookieBannerResize = () => {
            repositionCookieBanner('mobile-option-bar', 786);
        };

        // Parts pages intentionally use a normalized/consolidated canonical URL server-side for SEO purposes,
        // which can differ from the current filtered/search URL. On iOS, the native Share sheet reads the
        // canonical tag instead of the address bar, so keep it in sync with the page actually being viewed.
        syncCanonicalUrl(window.location.href);

        cookieBannerResize();
        window.addEventListener('resize', cookieBannerResize);

        return () => {
            window.removeEventListener('resize', cookieBannerResize);
        };
    }, []);

    useEffect(() => {

        const chunkParts = () => {
            const width = window.innerWidth;
            let chunkSize;
            if (width > 1250) {
                chunkSize = 4;
            } else if (width > 1000) {
                chunkSize = 2;
            } else {
                chunkSize = 1;
            }

            const chunks = [];
            for (let i = 0; i < props.PartsListings.length; i += chunkSize) {
                chunks.push(props.PartsListings.slice(i, i + chunkSize));
            }

            const noImageRow = chunks.map((chunk) => {
                const hasImage = chunk.some((part) => part.MediaModel.Media.length > 0);

                return !hasImage;
            });

            const mappedListings = props.PartsListings.map((part, index) => {
                const chunkIndex = Math.floor(index / chunkSize);
                return noImageRow[chunkIndex];
            });

            setListingPictureMap(mappedListings);
        }

        chunkParts();
        window.addEventListener('resize', chunkParts);

        return () => {
            window.removeEventListener('resize', chunkParts);
        };
    }, [windowSize.width]);

    const toggleMobileFilter = (skipSearch) => {
        let newShowMobileFilters = !showMobileFilters;
        setShowMobileFilters(newShowMobileFilters);
        if (newShowMobileFilters) {
            setMobileSearch(window.innerWidth <= 785);
        } else {
            setMobileSearch(false);
            if (!skipSearch) {
                runFacetedSearch(false);
            }
        }
    };

    const clearAll = () => {
        window.scrollTo(0, 0);
        window.location = props.ClearAllUrl;
    };

    const clearFacet = (fieldName, option) => {
        let searchUrl = buildUrl(false, []);

        //copy field name so we don't mutate 
        let queryStringParamName = (' ' + fieldName).slice(1);
        let selectedField = props.SelectedFacets.filter(f => f.FieldName == fieldName)[0];

        if (option && option.IsGroup && (selectedField.ParentNameForSearch && selectedField.ParentNameForSearch != '')) {
            queryStringParamName = selectedField.ParentNameForSearch;
        }

        if (option) {
            let qsValues = getURLParameter(searchUrl, queryStringParamName).split('|');
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);

            let filter = qsValues;
            let index = qsValues.indexOf(option.Value ? option.Value : option);
            if (index > -1) {
                filter.splice(index, 1);
            }

            if (filter.filter(o => o).length > 0) {
                searchUrl = insertURLParameter(searchUrl, queryStringParamName, filter.join('|'));
            }
        } else {
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);
        }

        if (fieldName.toLowerCase() == 'partnumber') {
            searchUrl = removeURLParameter(searchUrl, constants.format);
            searchUrl = removeURLParameter(searchUrl, constants.searchType);
        }

        if (fieldName.toLowerCase() == constants.model.toLocaleLowerCase()) {
            const manufacturerFacet = props.SelectedFacets.find(f => f.FieldName.toLowerCase() == constants.manufacturer.toLowerCase());
            const selectedManufacturer = manufacturerFacet?.Options?.find(opt => opt.Checked)?.Value;

            if (selectedManufacturer) {
                const manufacturerValue = encodeURIComponent(selectedManufacturer).toLowerCase();
                const manufacturerIndex = searchUrl.indexOf('/' + manufacturerValue);
                if (manufacturerIndex > -1) {
                    searchUrl = searchUrl.substring(0, manufacturerIndex + manufacturerValue.length + 1);
                }
            }
            
            const qsValues = props.SelectedFacets.filter(facet => {
                const facetName = facet.FieldName.toLowerCase();
                return !(facetName == constants.model.toLowerCase() || facetName == constants.manufacturer.toLowerCase());
            });

            if (qsValues.length > 0) {
                qsValues.forEach(facet => {
                    if (facet.Options && Array.isArray(facet.Options)) {
                        facet.Options.forEach(option => {
                            if (option.Checked) {
                                searchUrl = insertURLParameter(searchUrl, facet.FieldName, option.Value);
                            }
                        });
                    }
                });
            }     
        }

        if (searchUrl === routes.Parts_BaseSearch || fieldName.toLowerCase() == constants.manufacturer.toLocaleLowerCase()) {
            searchUrl = routes.Parts_Landing;
        }

        setSearchInProgress(true);
        window.location = searchUrl;
    };

    const clearFacetDebounced = useMemo(() => debounce(clearFacet, 300), [clearFacet]);

    const updatePendingChangesForSearch = (pendingChanges, latestFieldValue) => {
        let newField = true;
        for (var i = 0; i < pendingChanges.length; i++) {
            if (latestFieldValue.fieldName === pendingChanges[i].fieldName) {
                pendingChanges[i].value = latestFieldValue.value;
                newField = false;
                break;
            }
        }

        if (newField) {
            pendingChanges.push(latestFieldValue);
        }

        let fields = [...searchFields];
        for (var j = 0; j < fields.length; j++) {
            let field = fields[j];

            if (latestFieldValue.fieldName === field.FieldName && field.Type === 'Range') {
                // Update the range field in state so it doesn't do a weird re-render with the old value
                let splitValue = latestFieldValue.value.split('*');
                field.SelectedLowerBound = splitValue[0];
                field.SelectedUpperBound = splitValue[1];
                setSearchFields(fields);
                break;
            }
        }

        return pendingChanges;
    };

    const runFacetedSearch = (keepPage, latestFieldValue) => {
        setSearchInProgress(true);
        let pendingChanges = [];
        if (latestFieldValue) {
            if (Array.isArray(latestFieldValue)) {
                for (var i = 0; i < latestFieldValue.length; i++) {
                    pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue[i]);
                }
            } else {
                pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue);
            }
        }

        if (window.innerWidth <= 785) {
            setSearchInProgress(false);
            window.scrollTo(0, 0);
        }

        let searchUrl = buildUrl(keepPage, pendingChanges);
        if (pendingChanges.length > 0) {
            window.location = searchUrl;
        } else {
            setSearchInProgress(false);
        }
    };

    const buildUrl = (keepPage, pendingChanges) => {
        let searchUrl = props.SearchPageUrl;
        var keywordSearch = pendingChanges.find((f => f.fieldName.toLowerCase() === constants.keywords.toLowerCase()));

        pendingChanges.forEach((field) => {
            if (field.value) {
                searchUrl = insertURLParameter(searchUrl, field.fieldName, field.value);
                if (field.searchType) {
                    searchUrl = insertURLParameter(searchUrl, constants.searchType, field.searchType);
                }
            } else {
                searchUrl = removeURLParameter(searchUrl, field.fieldName);
                if (field.searchType) {
                    searchUrl = removeURLParameter(searchUrl, constants.searchType);
                }
            }

            if (field.fieldName.toLowerCase() == 'partnumber') {
                searchUrl = removeURLParameter(searchUrl, constants.format);
            }
        });

        if (keywordSearch) {
            if (currentSite?.Site?.IsHostedSite) {
                searchUrl = keywordSearch.value ? insertURLParameter(searchUrl, constants.keywords, keywordSearch.value) : routes.Parts_BaseSearch;
            } else {
                // Build base search URL when a keyword search is performed
                searchUrl = keywordSearch.value ? insertURLParameter(routes.Parts_BaseSearch, constants.keywords, keywordSearch.value) : routes.Parts_BaseSearch;


                const dsCompanyID = getURLParameter(window.location.search, constants.dsCompanyID);

                if (dsCompanyID) {
                    searchUrl = insertURLParameter(searchUrl, constants.dsCompanyID, dsCompanyID)
                }

                const locationID = getURLParameter(window.location.search, constants.locationID);

                if (locationID) {
                    searchUrl = insertURLParameter(searchUrl, constants.locationID, locationID)
                }
            }
        }
         
        searchUrl = removeURLParameter(searchUrl, constants.page);
        if (keepPage === true) {
            searchUrl = insertURLParameter(searchUrl, constants.page, props.PaginationModel.CurrentPage);
        }

        if (searchUrl === routes.Parts_BaseSearch) {
            searchUrl = routes.Parts_Landing;
        }

        return searchUrl;
    };

    const performSearch = (type, pageNumber) => {
        setSearchInProgress(true);

        let searchUrl = props.SearchPageUrl;
        if (partNumber) {
            searchUrl = insertURLParameter(searchUrl, 'PartNumber', partNumber);

            if (type) {
                searchUrl = insertURLParameter(searchUrl, 'SearchType', type);
            }
        }

        if (pageNumber && pageNumber > 1) {
            searchUrl = insertURLParameter(searchUrl, 'Page', pageNumber);
        }

        if (props.LocationID) {
            searchUrl = insertURLParameter(searchUrl, 'LocationID', props.LocationID);
        }

        if (props.DSCompanyID) {
            searchUrl = insertURLParameter(searchUrl, 'DSCompanyID', props.DSCompanyID);
        }

        if (props.OwnerCRMID) {
            searchUrl = insertURLParameter(searchUrl, 'OwnerCRMID', props.OwnerCRMID);
        }

        if (searchUrl.includes('?')) {
            if (props.PartsSource) {
                searchUrl = insertURLParameter(searchUrl, 'PartsSource', props.PartsSource);
            }
        }
        else if (searchUrl === routes.Parts_BaseSearch) {
            searchUrl = routes.Parts_Landing;
        }

        if (keywords) {
            searchUrl = insertURLParameter(searchUrl, 'Keywords', keywords);
        }

        window.location = searchUrl;
    };

    const handlePageNumberChange = (page) => {
        performSearch(props.SearchType, page)
    };

    const onEmailModalToggle = () => {
        if (returnFocusRef) {
            setTimeout(() => {
                returnFocusRef.focus();
            }, 150);
        }
        setShowEmailModal(showEmailModal => !showEmailModal);
    }

    const openEmailModal = (inquiry, contact, modalData) => {
        contact = contact || contactType.Retail;
        modalData = modalData || {};

        if (inquiry == inquiryType.EmailThis) {
            modalData.ModalTitle = translator.translate('sandhillssystem.share').toString();

            if (windowSize.isMobile && navigator.share) {
                navigator.share({
                    title: document.title,
                    url: window.location.href
                })
                    .then(() => gtmPushEvent(gtmEventType.MobileShare))
                    .catch(() => setShowShareButtons(true));
                return;
            } else {
                setShowShareButtons(true);
            }
        } else {
            setShowShareButtons(false);
        }

        setReturnFocusRef(modalData.Ref);
        setSelectedInquiry(inquiry);
        setSelectedContactType(contact);
        setShowEmailModal(showEmailModal => !showEmailModal);
        setSamModel({ ...samModel, ...modalData });
    };

    const onPrint = () => {
        gtmPushEvent(gtmEventType.printListDetail);
        setLoadImagesForPrint(true);
        window.print();
    };

    const renderBannerAd = (index) => {
        if (props.DoubleClickAdCollection?.AdUnits?.length > 0) {
            if (props.UseAlternateAdPlacement) {
                if (index === middleAdIndex && middleBannerAd) {
                    return (
                        <DoubleClickAd
                            {...props.DoubleClickAdCollection}
                            position={bannerAdPositions.Middle}
                            ad={middleBannerAd}
                            contained
                        />
                    );
                } else if (index === bottomAdIndex && bottomBannerAd) {
                    return (
                        <DoubleClickAd
                            {...props.DoubleClickAdCollection}
                            position={bannerAdPositions.Bottom}
                            ad={bottomBannerAd}
                            contained
                        />
                    );
                }
            } else if (index === middleAdIndex && bottomBannerAd) {
                return (
                    <DoubleClickAd
                        {...props.DoubleClickAdCollection}
                        position={bannerAdPositions.Bottom}
                        ad={bottomBannerAd}
                        contained
                    />
                );
            }
        }

        return (<></>);
    };

    return (
        <>
            <div id='parts-content' className={'parts-list-content' + (props.UseNewListPageDesign && !props.ShowLandingPage ? ' parts-redesign' : '')}>
                <section className='list-top-section'>
                    <div className='parts-title-and-breadcrumbs'>
                        {!props.IsHostedSite && <ComplexBreadcrumb BreadCrumbModels={props.BreadCrumbs} />}
                        {(!props.IsHostedSite && props.InventoryHeader) && !props.InventoryHeader?.EventID && !props.IsBothEventTypesDealerPage &&
                            <div className='dealer-tabs'>
                                <div className='dealer-tab-buttons cf'>
                                    {props.InventoryHeader.InventoryUrls && props.InventoryHeader.InventoryUrls.length > 0 && props.InventoryHeader.InventoryUrls.map(item =>
                                        <a key={item.DisplayText} className={'dealer-tab-button'} href={item.Url}>{item.DisplayText}</a>)}
                                </div>
                            </div>}
                        <div className='list-heading'>
                            <h1 className='parts-title'>
                                <span className='parts-title-text'><span dangerouslySetInnerHTML={{ __html: props.SearchTitle }}></span></span>
                            </h1>
                            {!props.ShowLandingPage && <span className='parts-listings-count'><span dangerouslySetInnerHTML={{ __html: props.ListingsCountTitle }}></span></span>}
                            <div className='media-buttons'>
                                {props.ShowPrintThis &&
                                    <button title={translator.translate('sandhills.print')} id='parts-list-page-print-this' type='button' className='print-this des-print-this ts-button' onClick={onPrint}>
                                        <i className='fas fa-print media-buttons__icon'></i>
                                    </button>}
                                {props.ShowEmailThis &&
                                    <button title={translator.translate('sandhillssystem.share')} id='parts-list-page-share-this' type='button' className='email-this des-email-this ts-button' onClick={() => openEmailModal(inquiryType.EmailThis)}>
                                        <i className='fas fa-share-alt media-buttons__icon'></i>
                                    </button>}
                            </div>
                        </div>
                    </div>
                    {topBannerAd && (!props.UseNewListPageDesign || props.ShowLandingPage) &&
                        <DoubleClickAd
                            {...props.DoubleClickAdCollection}
                            position={bannerAdPositions.Top}
                            ad={topBannerAd}
                            contained
                        />}
                    {props.BlurbModel && // future spot intended for blurbs 
                        <div className='parts-blurb'>
                            <p>
                               
                            </p>
                        </div>
                    }
                </section>
                {props.UseNewListPageDesign && windowSize.isMobile && !props.ShowLandingPage && (
                    <div>
                        <ListingOptions
                            setShowAllSpecs={setShowAllSpecs}
                            setExpandAllDescriptions={setExpandAllDescriptions}
                            showAllSpecs={showAllSpecs}
                            showFullDescription={expandAllDescriptions}
                            isMobile
                            IsAuthenticated={currentUser.IsAuthenticated}
                        />
                    </div>
                )}
                <section className={'bottom-section' + (props.ShowLandingPage ? '' : ' parts')}>
                    <div className='side-container'>
                        {!props.ShowLandingPage &&
                            <FacetedSearch
                                runSearch={runFacetedSearch}
                                clearAll={clearAll}
                                clearFacet={clearFacetDebounced}
                                disabled={searchInProgress}
                                showMobileFilters={showMobileFilters}
                                toggleMobileFilter={toggleMobileFilter}
                                mobileSearch={mobileSearch}
                                IsDefaultSearch={props.IsDefaultSearch}
                                SearchFields={searchFields}
                                SelectedFacets={props.SelectedFacets}
                                SearchBaseURL={props.SearchBaseUrl}
                                ListingsCount={props.PartsListings ? props.PartsListings.length : 0}
                            />}
                    </div>
                    <div id='main-content' className={'parts-list-content' + (props.ShowLandingPage ? ' no-parts' : '')}>
                        {props.ShowLandingPage &&
                            <div className='parts-search-container'>
                                <div className='parts-search-content'>
                                    <div className='parts-search'>
                                        <div className='parts-search-title'>
                                            <label htmlFor={props.ShouldShowPartNumberSearch ? 'PartNumber' : 'Keywords'}><strong><TranslationText tKey='sandhills.searchforparts' /></strong></label>
                                        </div>
                                        <PartsSearch
                                            partNumber={partNumber}
                                            setPartNumber={setPartNumber}
                                            keywords={keywords}
                                            setKeywords={setKeywords}
                                            inputId={props.ShouldShowPartNumberSearch ? 'PartNumber' : 'Keywords' }
                                            searchURL={props.SearchPageUrl}
                                            partsLandingURL={routes.Parts_Landing}
                                            formClass='parts-search-form'
                                            innerDivClass='part-search-partnumber-container'
                                            includeLabel={false}
                                            partsSearchInputClass='part-search-partnumber'
                                            partsSearchButtonClass='ts-button parts-search-submit'
                                            placeholder={props.ShouldShowPartNumberSearch ? translator.translate('sandhills.partnum') : translator.translate('sandhills.tradesites.mvc.partnumberorkeywords')}
                                            shouldShowPartNumberSearch={props.ShouldShowPartNumberSearch}
                                            showPartsLink={props.ShowNoPartNumberLink}
                                        />
                                    </div>
                                </div>
                                {props.CategoryLinks && props.CategoryLinks.length > 0 && (
                                    <div className='part-category-container'>
                                        {props.CategoryLinks.map((category) => (
                                            <PartsCategoryLinks
                                                key={category.CategoryID}
                                                {...category}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        }
                        {!props.ShowLandingPage &&
                            <>
                                {props.UseNewListPageDesign && windowSize.isNotMobile &&
                                    <div className='page-type-toggle-container'>
                                        <ListingOptions
                                            setShowAllSpecs={setShowAllSpecs}
                                            setExpandAllDescriptions={setExpandAllDescriptions}
                                            showAllSpecs={showAllSpecs}
                                            showFullDescription={expandAllDescriptions}
                                            IsAuthenticated={currentUser.IsAuthenticated}
                                        />
                                    </div>
                                }
                                {props.InventoryHeader &&
                                    <AdditionalPageInformation
                                        {...props.InventoryHeader}
                                        openEmailModal={openEmailModal}
                                        parts={true} />
                                }
                                {props.ErrorMessage && <div className='error-message'>{props.ErrorMessage}</div>}
                                <div className={'parts-listings' + (props.UseNewListPageDesign && newView ? ' grid' : '')}>
                                    {topBannerAd && props.UseNewListPageDesign &&
                                        <DoubleClickAd
                                            {...props.DoubleClickAdCollection}
                                            position={bannerAdPositions.Top}
                                            ad={topBannerAd}
                                            contained
                                        />
                                    }
                                    {(props.UseNewListPageDesign && newView) ?
                                        <>
                                            {props.PartsListings.map((item, index) =>

                                                <Fragment key={item.PartID}>
                                                    {item.GroupBannerText &&
                                                        <div className='part-group-banner'>{item.GroupBannerText}</div>}
                                                    <PartsGridListing
                                                        {...item}
                                                        partsSource={props.PartsSource}
                                                        openEmailModal={openEmailModal}
                                                        CustomEmailSellerTextForRetail={props.CustomEmailSellerTextForRetail}
                                                        ForceLoadImages={loadImagesForPrint}
                                                        NewView={newView}
                                                        NoImageRow={listingPictureMap[index] ?? item.NoImageRow}
                                                        showAllSpecs={showAllSpecs}
                                                        expandDescription={expandAllDescriptions}
                                                        AriaLabelTitle={item.Title + " (" + (index + 1) + ")"}
                                                    />
                                                    {renderBannerAd(index)}
                                                </Fragment>)}
                                        </>
                                        :
                                        <>
                                            {props.PartsListings.map((item, index) =>

                                                <Fragment key={item.PartID}>
                                                    {item.GroupBannerText &&
                                                        <div className='part-group-banner'>{item.GroupBannerText}</div>}
                                                    <PartsListListing
                                                        {...item}
                                                        partsSource={props.PartsSource}
                                                        openEmailModal={openEmailModal}
                                                        CustomEmailSellerTextForRetail={props.CustomEmailSellerTextForRetail}
                                                        ForceLoadImages={loadImagesForPrint}
                                                        NewView={newView}
                                                        AriaLabelTitle={item.PartNumber + " (" + (index + 1) + ")"}
                                                    />
                                                    {renderBannerAd(index)}
                                                </Fragment>)}
                                        </>
                                    }
                                </div>
                                {props.PaginationModel.TotalPages > 1 &&
                                    <Paging
                                        currentPage={props.PaginationModel.CurrentPage}
                                        totalPages={props.PaginationModel.TotalPages}
                                        onChange={handlePageNumberChange}
                                    />}
                            </>}
                    </div>
                    {props.ShowLandingPage && props.TopManufacturers.length > 0 && (
                        props.CategoryLinks?.length > 0 ? (
                            <div className='top-manufacturers'>
                                <div className='content'>
                                    <strong className='part-label'><TranslationText tKey='sandhills.tradepubwebsite.mvc.topmanufacturers' /></strong>
                                    <div className='flex-row'>
                                        {splitArrayEvenly(props.TopManufacturers, 4).map(group =>
                                            <div className='flex-col'>
                                                <ul className='links-list'>
                                                    {group.map(link =>
                                                        <li key={link.Url} className='links-container'>
                                                            <a href={link.Url} className='links-link'>{link.DisplayText}</a>
                                                        </li>)}
                                                </ul>
                                            </div>)}
                                    </div>
                                    {props.ViewAllManufacturers &&
                                        <div className='view-more-parts parts-all'>
                                            <a className='ts-button' href={props.ViewAllManufacturers.Url}>{props.ViewAllManufacturers.DisplayText}</a>
                                        </div>}
                                </div>
                            </div>
                        ) : (
                            <div className='top-manufacturers drilldown-section'>
                                <div className='content'>
                                    <strong className='part-label'><TranslationText tKey='sandhills.tradepubwebsite.mvc.topmanufacturers' /></strong>
                                    <div className='section-container'>
                                        <div className='sub-section-container'>
                                            <div className='sub-section-content'>
                                                {splitArrayEvenly(props.TopManufacturers, 4).map(group =>
                                                    <div className='links'>
                                                        {group.map(link =>
                                                            <div key={link.Url} className='links-container'>
                                                                <a href={link.Url} className='links-link'>{link.DisplayText}</a>
                                                            </div>)}
                                                    </div>)}
                                            </div>
                                        </div>
                                    </div>
                                    {props.ViewAllManufacturers &&
                                        <div className='view-more-parts parts-all'>
                                            <a href={props.ViewAllManufacturers.Url}>{props.ViewAllManufacturers.DisplayText}</a>
                                        </div>}
                                </div>
                            </div>
                        )
                    )}
                    {showEmailModal &&
                        <EmailModal
                            {...samModel}
                            toggleEmailModal={onEmailModalToggle}
                            selectedInquiry={selectedInquiry}
                            selectedContactType={selectedContactType}
                            DefaultCultureCode={props.DefaultCultureCode}
                            ShowSocialShareButtons={showShareButtons}
                        />}
                </section>
                {
                    (!currentSite.IsBetaSite && !props.UseNewListPageDesign) &&
                    <FacetedSearchMobileMenu
                        vip
                        sellUrl={props.SellYourEquipmentUrl}
                        filter={{
                            render: !props.ShowLandingPage,
                            toggle: toggleMobileFilter
                        }}
                        flyout={{
                            render: props.UseClassicViewFlyout,
                            url: props.ClassicFlyoutModel.ClassicViewURL,
                            img: props.ClassicFlyoutModel.ClassicViewImageURL
                        }}
                    />
                }
                {searchInProgress &&
                    <div className='searching-background' />}
                {(currentSite.IsMobileAppContext || (windowSize.isMobile && props.UseBottomMobilePadding)) &&
                    <div className='detail__mobile-padding-bottom' />
                }
            </div>
            {
                (currentSite.IsBetaSite || props.UseNewListPageDesign) && !props.ShowLandingPage &&
                <ListingControlBar
                    IsParts={true}
                    NoListings={props.PartsListings.length <= 0}
                    emailAlerts={currentSite.HasEmailAlerts}
                    IsBidCallerSite={currentSite?.Site?.IsBidcaller}
                    IsEF={props.IsEFEquipmentSearchPage}
                    IsAuctionSite={currentSite.IsAuctionSite}
                    IsHostedSite={props.IsHostedSite}
                    IsFramelessSite={currentSite.IsFramelessSite}
                    IsMobileAppContext={currentSite.IsMobileAppContext}
                    IsRY={currentSite?.Site?.IsRentalYard}
                    IsAV={currentSite?.Site?.IsAuctionValues}
                    ShowAV={currentSite?.Site?.IsController}
                    sellUrl={props.SellYourEquipmentUrl}
                    filter={{
                        render: !props.ShowLandingPage,
                        toggle: toggleMobileFilter
                    }} />
            }
        </>
    );
};

export default PartsListView;
global.PartsListView = PartsListView;